import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { Channel } from '../lib/types';
import { useChannels } from '../lib/channels-context';
import { useCurrentUser } from '../lib/auth';
import { CHANNEL_PALETTE } from '../lib/palette';

type Stats = Awaited<ReturnType<typeof api.adminStats>>;
type AdminRow = Awaited<ReturnType<typeof api.listAdmins>>[number];

export function Admin(): JSX.Element {
  const [stats, setStats] = useState<Stats | null>(null);
  const [error, setError] = useState<string>('');
  const { channels, refresh: refreshChannels } = useChannels();
  const me = useCurrentUser();
  const [creating, setCreating] = useState(false);
  const [newDraft, setNewDraft] = useState({
    channelId: '',
    name: '',
    tint: CHANNEL_PALETTE[0],
    description: '',
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<{ name: string; tint: string; description: string } | null>(null);
  const [toast, setToast] = useState('');
  const [admins, setAdmins] = useState<AdminRow[]>([]);
  const [adminHint, setAdminHint] = useState('');
  const [addingAdmin, setAddingAdmin] = useState(false);

  const flash = (m: string): void => {
    setToast(m);
    setTimeout(() => setToast(''), 2400);
  };

  const reloadAdmins = (): void => {
    api.listAdmins().then(setAdmins).catch(() => setAdmins([]));
  };

  useEffect(() => {
    api.adminStats().then(setStats).catch((e: Error) => setError(e.message));
    reloadAdmins();
  }, []);

  const addAdmin = async (): Promise<void> => {
    const hint = adminHint.trim();
    if (!hint) return flash('userId / email を入力してください');
    setAddingAdmin(true);
    try {
      const r = await api.addAdmin(hint);
      flash(`${r.displayName ?? r.userId} を管理者に追加しました`);
      setAdminHint('');
      reloadAdmins();
    } catch (e) {
      flash(`失敗: ${(e as Error).message}`);
    } finally {
      setAddingAdmin(false);
    }
  };

  const removeAdmin = async (a: AdminRow): Promise<void> => {
    if (a.userId === me.userId) return flash('自分自身は解除できません');
    const label = a.displayName ?? a.username ?? a.userId;
    if (!confirm(`${label} を管理者から外しますか？`)) return;
    try {
      await api.removeAdmin(a.userId);
      flash('管理者から外しました');
      reloadAdmins();
    } catch (e) {
      flash(`失敗: ${(e as Error).message}`);
    }
  };

  if (error.includes('403')) {
    return (
      <div style={{ padding: 30 }}>
        <div className="card" style={{ padding: 22, maxWidth: 520 }}>
          <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 16 }}>
            管理者権限がありません
          </div>
        </div>
      </div>
    );
  }

  const kpis = [
    { label: '総再生数', value: stats ? stats.totalViews.toLocaleString() : '—' },
    { label: '公開本数', value: stats ? `${stats.totalVideos}本` : '—' },
  ];

  const maxViews = Math.max(1, ...(stats?.topVideos ?? []).map((t) => t.views));

  const createChannel = async (): Promise<void> => {
    if (!newDraft.channelId || !newDraft.name) return flash('ID と名前は必須');
    try {
      await api.createChannel(newDraft);
      flash(`${newDraft.name} を追加しました`);
      setCreating(false);
      setNewDraft({ channelId: '', name: '', tint: '#1E3A6B', description: '' });
      void refreshChannels();
    } catch (e) {
      flash(`失敗: ${(e as Error).message}`);
    }
  };

  const startEdit = (ch: Channel): void => {
    setEditingId(ch.channelId);
    setEditDraft({ name: ch.name, tint: ch.tint, description: ch.description ?? '' });
  };

  const saveEdit = async (): Promise<void> => {
    if (!editingId || !editDraft) return;
    try {
      await api.updateChannel(editingId, editDraft);
      flash('保存しました');
      setEditingId(null);
      setEditDraft(null);
      void refreshChannels();
    } catch (e) {
      flash(`失敗: ${(e as Error).message}`);
    }
  };

  const deleteChannel = async (ch: Channel): Promise<void> => {
    const count = ch.count ?? 0;
    let cascade = false;
    if (count > 0) {
      const ok = confirm(
        `「${ch.name}」には ${count} 本の動画があります。動画も一緒に削除しますか？\n\n` +
          `[OK] 動画とチャンネルを削除\n[キャンセル] 何もしない`,
      );
      if (!ok) return;
      cascade = true;
    } else {
      if (!confirm(`「${ch.name}」を削除しますか？`)) return;
    }
    try {
      await api.deleteChannel(ch.channelId, cascade);
      flash(`${ch.name} を削除しました`);
      void refreshChannels();
    } catch (e) {
      flash(`失敗: ${(e as Error).message}`);
    }
  };

  return (
    <div style={{ padding: '26px 30px 60px' }}>
      <h1 style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 22, margin: '0 0 20px' }}>
        管理者ダッシュボード
      </h1>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 14, marginBottom: 26 }}>
        {kpis.map((s) => (
          <div key={s.label} className="card" style={{ padding: 18 }}>
            <div style={{ fontSize: 11.5, color: 'var(--ink-soft)', marginBottom: 8 }}>{s.label}</div>
            <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 26, lineHeight: 1 }}>{s.value}</div>
          </div>
        ))}
      </div>
      <div className="card" style={{ padding: 20, borderRadius: 16, marginBottom: 18 }}>
        <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 14, marginBottom: 18 }}>
          よく見られている動画
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {(stats?.topVideos ?? []).map((t) => (
            <div key={t.videoId}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 6 }}>
                <span style={{ fontSize: 12.5, fontWeight: 500, flex: 1 }}>{t.title}</span>
                <span style={{ fontSize: 12, color: 'var(--ink-soft)', fontVariantNumeric: 'tabular-nums' }}>{t.views}回</span>
              </div>
              <div style={{ height: 8, borderRadius: 4, background: 'var(--surface-hover)' }}>
                <div style={{ height: 8, borderRadius: 4, background: t.tint, width: `${(t.views / maxViews) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card" style={{ padding: 20, borderRadius: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 14 }}>チャンネル管理</div>
          <button className="btn" style={{ marginLeft: 'auto' }} onClick={() => setCreating((v) => !v)}>
            {creating ? '閉じる' : '＋ 追加'}
          </button>
        </div>

        {creating && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, padding: 14, background: 'var(--surface-sunk)', borderRadius: 10, marginBottom: 14 }}>
            <label style={fld}>
              <span style={lbl}>ID (英小文字・数字・-)</span>
              <input className="input" value={newDraft.channelId} onChange={(e) => setNewDraft({ ...newDraft, channelId: e.target.value })} placeholder="例: pdm" />
            </label>
            <label style={fld}>
              <span style={lbl}>名前</span>
              <input className="input" value={newDraft.name} onChange={(e) => setNewDraft({ ...newDraft, name: e.target.value })} placeholder="例: PdM 定例" />
            </label>
            <div style={fld}>
              <span style={lbl}>色</span>
              <PaletteSwatch value={newDraft.tint} onChange={(t) => setNewDraft({ ...newDraft, tint: t })} />
            </div>
            <label style={{ ...fld, gridColumn: '1 / -1' }}>
              <span style={lbl}>説明</span>
              <textarea className="input" value={newDraft.description} onChange={(e) => setNewDraft({ ...newDraft, description: e.target.value })} style={{ minHeight: 60, resize: 'vertical' }} />
            </label>
            <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn" onClick={createChannel}>作成</button>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {channels.map((ch) => {
            const editing = editingId === ch.channelId && editDraft;
            return (
              <div
                key={ch.channelId}
                style={{
                  display: 'flex',
                  alignItems: editing ? 'flex-start' : 'center',
                  gap: 12,
                  padding: '10px 12px',
                  border: '1px solid var(--border-soft)',
                  borderRadius: 10,
                }}
              >
                <span
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: 4,
                    background: editing ? editDraft.tint : ch.tint,
                    flex: 'none',
                    marginTop: editing ? 12 : 0,
                  }}
                />
                {editing ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <input
                        className="input"
                        value={editDraft.name}
                        onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                        placeholder="名前"
                      />
                      <input
                        className="input"
                        value={editDraft.description}
                        onChange={(e) => setEditDraft({ ...editDraft, description: e.target.value })}
                        placeholder="説明"
                      />
                    </div>
                    <PaletteSwatch
                      value={editDraft.tint}
                      onChange={(t) => setEditDraft({ ...editDraft, tint: t })}
                    />
                  </div>
                ) : (
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>{ch.name}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--ink-quiet)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {ch.channelId} · 動画 {ch.count ?? 0}本
                    </div>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 6 }}>
                  {editing ? (
                    <>
                      <button onClick={() => { setEditingId(null); setEditDraft(null); }} className="btn-ghost">キャンセル</button>
                      <button onClick={saveEdit} className="btn">保存</button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => startEdit(ch)} style={smallBtn}>編集</button>
                      <button onClick={() => deleteChannel(ch)} style={{ ...smallBtn, color: '#B23A3A' }}>削除</button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="card" style={{ padding: 20, borderRadius: 16, marginTop: 18 }}>
        <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 14, marginBottom: 12 }}>
          管理者
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <input
            className="input"
            value={adminHint}
            onChange={(e) => setAdminHint(e.target.value)}
            placeholder="userId (例: hisahiro) / email / Cognito sub"
            style={{ flex: 1 }}
            onKeyDown={(e) => e.key === 'Enter' && void addAdmin()}
          />
          <button className="btn" onClick={addAdmin} disabled={addingAdmin} style={{ opacity: addingAdmin ? 0.6 : 1 }}>
            {addingAdmin ? '追加中…' : '追加'}
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {admins.length === 0 && (
            <div style={{ fontSize: 12.5, color: 'var(--ink-quiet)' }}>管理者はまだいません。</div>
          )}
          {admins.map((a) => {
            const isSelf = a.userId === me.userId;
            const label = a.displayName ?? a.username ?? a.email ?? a.userId;
            const sub = [a.username, a.email].filter(Boolean).join(' · ') || a.userId;
            return (
              <div
                key={a.userId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 12px',
                  border: '1px solid var(--border-soft)',
                  borderRadius: 10,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>
                    {label}
                    {isSelf && (
                      <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--brand)', fontWeight: 500 }}>
                        (自分)
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--ink-quiet)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {sub}
                  </div>
                </div>
                <button
                  onClick={() => removeAdmin(a)}
                  disabled={isSelf}
                  style={{ ...smallBtn, color: isSelf ? 'var(--ink-quiet)' : '#B23A3A', opacity: isSelf ? 0.5 : 1, cursor: isSelf ? 'not-allowed' : 'pointer' }}
                >
                  外す
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {toast && (
        <div style={toastStyle}>{toast}</div>
      )}
    </div>
  );
}

function PaletteSwatch({
  value,
  onChange,
}: {
  value: string;
  onChange: (color: string) => void;
}): JSX.Element {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {CHANNEL_PALETTE.map((c) => {
        const active = c.toLowerCase() === value.toLowerCase();
        return (
          <button
            key={c}
            type="button"
            onClick={() => onChange(c)}
            aria-label={c}
            style={{
              width: 24,
              height: 24,
              borderRadius: 6,
              background: c,
              border: active ? '2px solid var(--ink)' : '1px solid var(--border)',
              cursor: 'pointer',
              padding: 0,
              boxShadow: active ? '0 0 0 2px var(--surface)' : undefined,
            }}
          />
        );
      })}
    </div>
  );
}

const fld: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6 };
const lbl: React.CSSProperties = { fontSize: 12, fontWeight: 700 };
const smallBtn: React.CSSProperties = {
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  fontSize: 12,
  padding: '6px 10px',
  borderRadius: 8,
  cursor: 'pointer',
  color: 'var(--ink-mid)',
};
const toastStyle: React.CSSProperties = {
  position: 'fixed',
  bottom: 26,
  left: '50%',
  transform: 'translateX(-50%)',
  background: 'var(--ink)',
  color: '#fff',
  fontSize: 13,
  padding: '13px 22px',
  borderRadius: 999,
  boxShadow: '0 8px 24px rgba(31,42,68,0.16)',
  zIndex: 40,
};
